import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { ConversationLiveState } from "@/api/db/messaging-read";
import type { ConversationAttachment } from "@/types/conversation";
import { orpc } from "@/client";
import { BrowserFile } from "@/lib/browser-file";
import { MessageCache } from "@/lib/message-cache";
import { useConversationSession } from "@/hooks/use-conversation-session";

export function useConversationSend(personId: number, state: ConversationLiveState | undefined) {
	const queryClient = useQueryClient();
	const { session, draft } = useConversationSession(personId);

	const { mutate } = useMutation({
		mutationFn: async (input: {
			operationId: string;
			text: string;
			revision: number;
			attachments: ConversationAttachment[];
			kind: "image" | "audio";
			draftText: string | undefined;
		}) => {
			if (input.attachments.length === 0) {
				const result = await orpc.send.message.call({
					operation_id: input.operationId,
					person_id: personId,
					channel: "free",
					text: input.text,
					draft_text: input.draftText,
				});
				session.patch({ sendResult: result });

				if (result.outcome === "blocked") {
					session.releaseSendIntent();
				} else {
					session.clearSent(input.revision);
				}

				return;
			}

			for (const [index, attachment] of input.attachments.entries()) {
				const data = await BrowserFile.toBase64(attachment.file);

				if (!session.active) {
					return;
				}

				const result = await orpc.send.message.call({
					operation_id: attachment.id,
					person_id: personId,
					channel: "free",
					text: input.kind === "image" && index === 0 ? input.text : "",
					media: { id: attachment.id, kind: input.kind, data },
				});
				session.patch({ sendResult: result });

				if (result.outcome === "blocked") {
					session.renewAttachment(attachment.id);
					return;
				}

				if (input.kind === "audio") {
					if (session.state.audio?.id === attachment.id) {
						session.removeAudio();
					}
				} else {
					session.removeImage(attachment.id);
					session.clearSent(input.revision);
				}
			}
		},
		onMutate: () => session.patch({ sending: true, sendError: null, sendResult: null }),
		onError: () =>
			session.patch({
				sendError:
					"Não foi possível confirmar o registro. O rascunho foi mantido; tentar de novo consulta o mesmo envio.",
			}),
		onSettled: () => {
			session.patch({ sending: false });
			void MessageCache.for(queryClient)
				.person(personId)
				.catch(() => null);
			void queryClient.invalidateQueries({ queryKey: orpc.messages.list.key() });
			void queryClient.invalidateQueries({ queryKey: orpc.realtime.inbox.key() });
		},
	});

	const send = (kind: "text" | "audio") => {
		if (session.state.sending || !state?.window_open) {
			return;
		}

		if (kind === "audio") {
			if (!session.state.audio || Date.now() < session.state.audio.sendAvailableAt) {
				return;
			}

			session.patch({ sending: true });
			mutate({
				operationId: session.state.audio.id,
				text: "",
				revision: draft.revision,
				attachments: [session.state.audio],
				kind: "audio",
				draftText: undefined,
			});
			return;
		}

		if (session.state.audio || (!draft.text.trim() && draft.images.length === 0)) {
			return;
		}

		session.patch({ sending: true });
		mutate({
			operationId: session.sendIntent(),
			text: draft.text,
			revision: draft.revision,
			attachments: draft.images,
			kind: "image",
			draftText: draft.suggestion?.draft,
		});
	};

	return { send };
}
